import { Invoice } from "../types/invoice";
import { Schedule } from "../store/appStore";
import { NoticeOfAssignment } from "../types/schema";
import { calculatePaymentSplit } from "./payment-utils";
import { buildWaterfall } from "./cashflow-waterfall";
import { endOfMonth, isWithinInterval, parseISO, startOfMonth } from "date-fns";

export const isActiveRS = (s: Schedule) => s.status === "Approved" || s.status === "Active";
export const isUnpaid = (inv: Invoice) =>
  inv.payments.reduce((s, p) => s + p.amount, 0) < inv.grandTotalRounded;

export const monthlyRental = (s: Schedule): number => {
  const reg = (s.lines || []).find((l) => !l.isInterim);
  return reg?.baseRentRounded || 0;
};

export const remainingBaseForRS = (s: Schedule): { amount: number; count: number } => {
  const today = new Date();
  const lines = (s.lines || []).filter((l) => !l.isInterim && parseISO(l.dueDate) >= today);
  return { amount: lines.reduce((a, l) => a + (l.baseRentRounded || 0), 0), count: lines.length };
};

export const invoicesForRS = (invoices: Invoice[], rsId: string) =>
  invoices.filter((inv) => inv.scheduleId === rsId);

/** Astrafin share of a single payment on an invoice */
const astraShareOfPayment = (inv: Invoice, paid: number): number => {
  if (inv.grandTotalRounded <= 0) return 0;
  const split = calculatePaymentSplit(inv);
  return Math.round(paid * (split.payToAstrafin / inv.grandTotalRounded));
};

/** Lender share of a single payment */
const lenderShareOfPayment = (inv: Invoice, paid: number): number => {
  if (inv.grandTotalRounded <= 0) return 0;
  const split = calculatePaymentSplit(inv);
  return Math.round(paid * (split.payToFinancier / inv.grandTotalRounded));
};

export const astraCashflowToDate = (invs: Invoice[]): number =>
  invs.reduce(
    (sum, inv) =>
      sum +
      inv.payments.reduce((a, p) => a + astraShareOfPayment(inv, p.amount), 0),
    0
  );

export const lenderCashflowToDate = (invs: Invoice[]): number =>
  invs.reduce(
    (sum, inv) =>
      sum +
      inv.payments.reduce((a, p) => a + lenderShareOfPayment(inv, p.amount), 0),
    0
  );

export const astraCashflowInWindow = (
  invs: Invoice[],
  from: Date,
  to: Date
): number =>
  invs.reduce(
    (sum, inv) =>
      sum +
      inv.payments.reduce((a, p) => {
        const d = parseISO(p.date);
        return d >= from && d <= to ? a + astraShareOfPayment(inv, p.amount) : a;
      }, 0),
    0
  );

export interface PortfolioKPIs {
  activePortfolioAmount: number;
  activePortfolioCount: number; // remaining unpaid rentals across active RS
  outstandingAmount: number;
  outstandingCount: number;
  overdueAmount: number;
  overdueCount: number;
  thisMonthBilling: number;
  thisMonthAstraCashflow: number;
  capitalDeployed: number;
  capitalRecovered: number;
  astraCashflowToDate: number;
  astraCashflowRentalsCount: number;
  tdsAccumulatedToDate: number;
  revenueBookedToDate: number;
}

export function computePortfolioKPIs(
  schedules: Schedule[],
  invoices: Invoice[],
  noAs: NoticeOfAssignment[]
): PortfolioKPIs {
  const today = new Date();
  const mStart = startOfMonth(today);
  const mEnd = endOfMonth(today);

  let activeAmt = 0, activeCnt = 0;
  schedules.filter(isActiveRS).forEach((s) => {
    const r = remainingBaseForRS(s);
    activeAmt += r.amount;
    activeCnt += r.count;
  });

  const unpaid = invoices.filter(isUnpaid);
  const outstandingAmount = unpaid.reduce(
    (s, inv) => s + (inv.grandTotalRounded - inv.payments.reduce((a, p) => a + p.amount, 0)),
    0
  );
  const overdue = unpaid.filter((inv) => parseISO(inv.dueDate) < today);
  const overdueAmount = overdue.reduce(
    (s, inv) => s + (inv.grandTotalRounded - inv.payments.reduce((a, p) => a + p.amount, 0)),
    0
  );

  const monthInvs = invoices.filter((inv) =>
    isWithinInterval(parseISO(inv.invoiceDate), { start: mStart, end: mEnd })
  );
  // Rental + Interim (interim invoices have type "Advance") only
  const rentalMonthInvs = monthInvs.filter(
    (inv) => inv.type === "Rental" || inv.type === "Advance"
  );
  const thisMonthBilling = rentalMonthInvs.reduce((s, inv) => s + inv.grandTotalRounded, 0);
  const thisMonthAstraCashflow = rentalMonthInvs.reduce(
    (s, inv) => s + calculatePaymentSplit(inv).payToAstrafin,
    0
  );

  // Aggregate waterfall-based capital & cashflow figures across all schedules
  let capitalDeployed = 0;
  let capitalRecovered = 0;
  let astraCftd = 0;
  let tdsAccumulatedToDate = 0;
  let revenueBookedToDate = 0;
  schedules.forEach((s) => {
    const n = noAs.find((x) => x.scheduleId === s.id);
    const linked = invoicesForRS(invoices, s.id);
    const wf = buildWaterfall({ schedule: s, noa: n, invoices: linked });
    capitalDeployed += wf.totals.capitalDeployed;
    capitalRecovered += wf.totals.capitalRecovered;
    astraCftd += wf.totals.astraToDate;
    const pastRows = wf.rows.filter((r) => r.isPast);
    tdsAccumulatedToDate += pastRows.reduce((sum, r) => sum + r.tds, 0);
    revenueBookedToDate += pastRows.length ? pastRows[pastRows.length - 1].cmCashflowToAstrafin : 0;
  });

  const rentalsWithAstraReceipt = invoices.filter(
    (inv) => inv.payments.length > 0 && calculatePaymentSplit(inv).payToAstrafin > 0
  ).length;


  return {
    activePortfolioAmount: Math.round(activeAmt),
    activePortfolioCount: activeCnt,
    outstandingAmount: Math.round(outstandingAmount),
    outstandingCount: unpaid.length,
    overdueAmount: Math.round(overdueAmount),
    overdueCount: overdue.length,
    thisMonthBilling: Math.round(thisMonthBilling),
    thisMonthAstraCashflow: Math.round(thisMonthAstraCashflow),
    capitalDeployed: Math.round(capitalDeployed),
    capitalRecovered: Math.round(capitalRecovered),
    astraCashflowToDate: Math.round(astraCftd),
    astraCashflowRentalsCount: rentalsWithAstraReceipt,
    tdsAccumulatedToDate: Math.round(tdsAccumulatedToDate),
    revenueBookedToDate: Math.round(revenueBookedToDate),
  };
}

export interface PortfolioRowData {
  rs: Schedule;
  noa?: NoticeOfAssignment;
  monthlyRental: number;
  tenureMonths: number;
  rentalStartDate: string;
  upfrontRevenue: number; // PF + GST (recognized)
  cashflowThisMonth: number;
  cashflowToDate: number;
  status: string;
}

/** Flat row shape returned by portfolio cashflow list API. */
export interface PortfolioScheduleListItem {
  rsId: string;
  rsNo: string;
  customerName: string;
  lenderName: string | null;
  monthlyRental: number;
  tenureMonths: number;
  rentalStartDate: string;
  upfrontRevenue: number;
  cashflowThisMonth: number;
  cashflowToDate: number;
  status: string;
}

const portfolioNum = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const portfolioPick = (obj: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

export function normalizePortfolioScheduleListItem(
  raw: Record<string, unknown>
): PortfolioScheduleListItem {
  const config =
    raw.config && typeof raw.config === "object"
      ? (raw.config as Record<string, unknown>)
      : undefined;
  const customer =
    config?.customer && typeof config.customer === "object"
      ? (config.customer as Record<string, unknown>)
      : raw.customer && typeof raw.customer === "object"
        ? (raw.customer as Record<string, unknown>)
        : undefined;
  const noa =
    raw.noa && typeof raw.noa === "object"
      ? (raw.noa as Record<string, unknown>)
      : undefined;
  const upfront =
    raw.upfront && typeof raw.upfront === "object"
      ? (raw.upfront as Record<string, unknown>)
      : undefined;
  const lines = Array.isArray(raw.lines) ? raw.lines : [];
  const monthlyLine = lines.find(
    (line) =>
      line &&
      typeof line === "object" &&
      !(line as Record<string, unknown>).isInterim
  ) as Record<string, unknown> | undefined;

  const nestedCustomerName = customer
    ? String(
        portfolioPick(customer, "company_name", "companyName", "name") ?? ""
      )
    : typeof raw.customer === "string"
      ? raw.customer
      : "";

  const rsId = String(
    portfolioPick(
      raw,
      "rsId",
      "rs_id",
      "id",
      "rentalScheduleId",
      "rental_schedule_id",
      "scheduleId",
      "schedule_id"
    ) ?? ""
  );
  const rsNo = String(
    portfolioPick(
      raw,
      "rsNo",
      "rs_no",
      "displayId",
      "display_id",
      "rentalScheduleCode",
      "rental_schedule_code"
    ) ?? ""
  );

  const lenderRaw =
    portfolioPick(noa ?? {}, "lenderName", "lender_name", "lender") ??
    portfolioPick(raw, "lenderName", "lender_name", "lender", "assignedLender", "assigned_lender");

  const monthlyRentalFromLine = monthlyLine
    ? portfolioPick(monthlyLine, "baseRentRounded", "base_rent_rounded")
    : undefined;

  const upfrontFromCharges =
    portfolioNum(upfront?.processingFeeRounded ?? upfront?.processing_fee_rounded) +
    portfolioNum(upfront?.feeGSTRounded ?? upfront?.fee_gst_rounded);

  const buyBackDate = portfolioPick(
    config ?? {},
    "buy_back_date",
    "buyBackDate"
  );
  const rawStatus = String(raw.status ?? "").toLowerCase();
  const noaStatus = String(noa?.status ?? "").toLowerCase();
  let status = String(raw.status ?? "");
  if (rawStatus === "draft") {
    status = "Draft";
  } else if (rawStatus === "approved" && noaStatus === "approved" && buyBackDate) {
    const today = new Date();
    const lastDate = new Date(String(buyBackDate));
    if (!Number.isNaN(lastDate.getTime())) {
      status = today < lastDate ? "Active" : "Matured";
    }
  } else if (status) {
    status = status.charAt(0).toUpperCase() + status.slice(1);
  }

  return {
    rsId,
    rsNo: rsNo || rsId,
    customerName: String(
      portfolioPick(raw, "customerName", "customer_name") ?? nestedCustomerName
    ),
    lenderName: lenderRaw == null || lenderRaw === "" ? null : String(lenderRaw),
    monthlyRental: portfolioNum(
      portfolioPick(raw, "monthlyRental", "monthly_rental") ?? monthlyRentalFromLine
    ),
    tenureMonths: portfolioNum(
      portfolioPick(config ?? raw, "tenureMonths", "tenure_months", "tenure")
    ),
    rentalStartDate: String(
      portfolioPick(
        config ?? raw,
        "startDate",
        "start_date",
        "commencementDate",
        "commencement_date",
        "rentalStartDate",
        "rental_start_date"
      ) ?? ""
    ),
    upfrontRevenue:
      portfolioNum(portfolioPick(raw, "upfrontRevenue", "upfront_revenue")) ||
      upfrontFromCharges,
    cashflowThisMonth: portfolioNum(
      portfolioPick(raw, "cashflowThisMonth", "cashflow_this_month")
    ),
    cashflowToDate: portfolioNum(
      portfolioPick(raw, "cashflowToDate", "cashflow_to_date")
    ),
    status,
  };
}

export function normalizePortfolioScheduleListResponse(
  payload: unknown
): PortfolioScheduleListItem[] {
  if (!payload || typeof payload !== "object") return [];

  const root = payload as Record<string, unknown>;
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : undefined;

  const rows = Array.isArray(root.schedules)
    ? root.schedules
    : Array.isArray(data?.schedules)
      ? data.schedules
      : Array.isArray(root.cashflowList)
        ? root.cashflowList
        : Array.isArray(data?.cashflowList)
          ? data.cashflowList
          : Array.isArray(root.rentalScheduleCashflowList)
            ? root.rentalScheduleCashflowList
            : Array.isArray(data?.rentalScheduleCashflowList)
              ? data.rentalScheduleCashflowList
              : Array.isArray(root.rental_schedule_cashflow_list)
                ? root.rental_schedule_cashflow_list
                : Array.isArray(data?.rental_schedule_cashflow_list)
                  ? data.rental_schedule_cashflow_list
                  : Array.isArray(root.list)
                    ? root.list
                    : Array.isArray(data?.list)
                      ? data.list
                      : Array.isArray(root.data)
                        ? root.data
                        : Array.isArray(root.rows)
                          ? root.rows
                          : Array.isArray(data?.rows)
                            ? data.rows
                            : [];

  return rows.map((row) =>
    normalizePortfolioScheduleListItem(row as Record<string, unknown>)
  );
}

export interface PortfolioCashflowCustomer {
  customerId: string;
  customerName: string;
}

export function normalizePortfolioCashflowCustomersResponse(
  payload: unknown
): PortfolioCashflowCustomer[] {
  if (!payload || typeof payload !== "object") return [];

  const root = payload as Record<string, unknown>;
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : undefined;

  const rows = Array.isArray(root.customers)
    ? root.customers
    : Array.isArray(data?.customers)
      ? data.customers
      : Array.isArray(root.data)
        ? root.data
        : [];

  const customers = rows
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const r = row as Record<string, unknown>;
      const customerId = String(
        portfolioPick(r, "customerId", "customer_id") ?? ""
      ).trim();
      const customerName = String(
        portfolioPick(r, "customerName", "customer_name") ?? ""
      ).trim();
      if (!customerId || !customerName) return null;
      return { customerId, customerName };
    })
    .filter((row): row is PortfolioCashflowCustomer => row !== null);

  return customers.sort((a, b) =>
    a.customerName.localeCompare(b.customerName, undefined, { sensitivity: "base" })
  );
}

export type PortfolioRSLineStatusTone = "default" | "secondary" | "destructive";

export interface PortfolioRSLineItem {
  scheduleLineId: string;
  periodLabel: string;
  fromDate: string;
  toDate: string;
  dueDate: string;
  baseRentRounded: number;
  gstRounded: number;
  tdsRounded: number;
  totalRounded: number;
  statusLabel: string;
  statusTone: PortfolioRSLineStatusTone;
}

export interface PortfolioRSDetailData {
  id: string;
  displayId: string;
  status: string;
  customerName: string;
  creditRating: string;
  totalRentalSchedules: number;
  nextDueDate: string | null;
  monthlyRental: number;
  fundingType: string;
  lenderName: string | null;
  disbursementDate: string | null;
  disbursementAmount: number;
  tenureMonths: number;
  upfrontReceived: number;
  upfrontRevenue: number;
  totalBilling: number;
  totalReceived: number;
  totalOutstanding: number;
  tdsTotal: number;
  itcInflow: number;
  itcUtilised: number;
  revenueBookedToDate: number;
  revenueThisTranche: number;
  capitalDeployed: number;
  capitalRecovered: number;
  lines: PortfolioRSLineItem[];
}

const formatPortfolioStatus = (value: unknown) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

export function normalizePortfolioRSDetailResponse(
  payload: unknown
): PortfolioRSDetailData | null {
  if (!payload || typeof payload !== "object") return null;

  const rootObj = payload as Record<string, unknown>;
  const root =
    rootObj.data && typeof rootObj.data === "object"
      ? (rootObj.data as Record<string, unknown>)
      : rootObj;

  const id = String(
    portfolioPick(root, "id", "scheduleId", "schedule_id", "rsId", "rs_id") ?? ""
  );
  if (!id) return null;

  const lenderRaw = portfolioPick(root, "lenderName", "lender_name", "lender");

  return {
    id,
    displayId: String(
      portfolioPick(root, "displayId", "display_id", "rsNo", "rs_no") ?? ""
    ),
    status: formatPortfolioStatus(root.status),
    customerName: String(
      portfolioPick(root, "customerName", "customer_name") ?? ""
    ),
    creditRating: String(
      portfolioPick(root, "creditRating", "credit_rating") ?? "Not Rated"
    ),
    totalRentalSchedules: portfolioNum(
      portfolioPick(root, "totalRentalSchedules", "total_rental_schedules")
    ),
    nextDueDate:
      (portfolioPick(root, "nextDueDate", "next_due_date") as string | undefined) ??
      null,
    monthlyRental: portfolioNum(
      portfolioPick(root, "monthlyRental", "monthly_rental")
    ),
    fundingType: String(portfolioPick(root, "fundingType", "funding_type") ?? "—"),
    lenderName:
      lenderRaw == null || lenderRaw === "" ? null : String(lenderRaw),
    disbursementDate:
      (portfolioPick(root, "disbursementDate", "disbursement_date") as
        | string
        | undefined) ?? null,
    disbursementAmount: portfolioNum(
      portfolioPick(root, "disbursementAmount", "disbursement_amount")
    ),
    tenureMonths: portfolioNum(
      portfolioPick(root, "tenureMonths", "tenure_months", "tenure")
    ),
    upfrontReceived: portfolioNum(
      portfolioPick(root, "upfrontReceived", "upfront_received")
    ),
    upfrontRevenue: portfolioNum(
      portfolioPick(root, "upfrontRevenue", "upfront_revenue")
    ),
    totalBilling: portfolioNum(
      portfolioPick(root, "totalBilling", "total_billing")
    ),
    totalReceived: portfolioNum(
      portfolioPick(root, "totalReceived", "total_received")
    ),
    totalOutstanding: portfolioNum(
      portfolioPick(root, "totalOutstanding", "total_outstanding")
    ),
    tdsTotal: portfolioNum(portfolioPick(root, "tdsTotal", "tds_total")),
    itcInflow: portfolioNum(portfolioPick(root, "itcInflow", "itc_inflow")),
    itcUtilised: portfolioNum(
      portfolioPick(
        root,
        "itcUtilised",
        "itc_utilised",
        "itcUtilized",
        "itc_utilized"
      )
    ),
    revenueBookedToDate: portfolioNum(
      portfolioPick(root, "revenueBookedToDate", "revenue_booked_to_date")
    ),
    revenueThisTranche: portfolioNum(
      portfolioPick(root, "revenueThisTranche", "revenue_this_tranche")
    ),
    capitalDeployed: portfolioNum(
      portfolioPick(root, "capitalDeployed", "capital_deployed")
    ),
    capitalRecovered: portfolioNum(
      portfolioPick(root, "capitalRecovered", "capital_recovered")
    ),
    lines: [],
  };
}

const lineStatusTone = (label: string): PortfolioRSLineStatusTone => {
  const lower = label.toLowerCase();
  if (lower === "paid") return "default";
  if (lower === "overdue") return "destructive";
  return "secondary";
};

const formatLineStatusLabel = (value: unknown) => {
  const raw = String(value ?? "Not Invoiced").trim();
  if (!raw) return "Not Invoiced";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const normalizePortfolioRSLine = (raw: Record<string, unknown>): PortfolioRSLineItem => {
  const isInterim = Boolean(portfolioPick(raw, "isInterim", "is_interim"));
  const periodNo = portfolioPick(raw, "periodNo", "period_no");
  const periodValue = String(periodNo ?? "").trim().toLowerCase();
  const isInterimLine = isInterim || periodValue === "i";
  const statusLabel = formatLineStatusLabel(
    portfolioPick(raw, "status", "invoiceStatus", "invoice_status")
  );

  return {
    scheduleLineId: String(
      portfolioPick(raw, "scheduleLineId", "schedule_line_id", "id") ?? ""
    ),
    periodLabel: isInterimLine
      ? "Interim"
      : periodNo != null && periodValue !== ""
        ? `#${periodNo}`
        : "—",
    fromDate: String(portfolioPick(raw, "fromDate", "from_date") ?? ""),
    toDate: String(portfolioPick(raw, "toDate", "to_date") ?? ""),
    dueDate: String(portfolioPick(raw, "dueDate", "due_date") ?? ""),
    baseRentRounded: portfolioNum(
      portfolioPick(raw, "baseRentRounded", "base_rent_rounded")
    ),
    gstRounded: portfolioNum(portfolioPick(raw, "gstRounded", "gst_rounded")),
    tdsRounded: portfolioNum(portfolioPick(raw, "tdsRounded", "tds_rounded")),
    totalRounded: portfolioNum(
      portfolioPick(raw, "totalRounded", "total_rounded")
    ),
    statusLabel,
    statusTone: lineStatusTone(statusLabel),
  };
};

export function normalizeRentalScheduleLinesResponse(
  payload: unknown
): PortfolioRSLineItem[] {
  if (!payload || typeof payload !== "object") return [];

  const root = payload as Record<string, unknown>;
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : undefined;

  const lines = Array.isArray(root.lines)
    ? root.lines
    : Array.isArray(data?.lines)
      ? data.lines
      : [];

  return lines.map((line) =>
    normalizePortfolioRSLine(line as Record<string, unknown>)
  );
}

export function normalizeRentalScheduleCashflowDetail(
  payload: unknown
): PortfolioRSDetailData | null {
  if (!payload || typeof payload !== "object") return null;

  const rootObj = payload as Record<string, unknown>;
  const root =
    rootObj.data && typeof rootObj.data === "object"
      ? (rootObj.data as Record<string, unknown>)
      : rootObj;

  const nestedSchedule =
    root.schedule && typeof root.schedule === "object"
      ? (root.schedule as Record<string, unknown>)
      : root.rentalSchedule && typeof root.rentalSchedule === "object"
        ? (root.rentalSchedule as Record<string, unknown>)
        : root.rental_schedule && typeof root.rental_schedule === "object"
          ? (root.rental_schedule as Record<string, unknown>)
          : undefined;

  const schedule = (nestedSchedule ?? root) as Record<string, unknown>;

  const config =
    schedule.config && typeof schedule.config === "object"
      ? (schedule.config as Record<string, unknown>)
      : undefined;
  const customer =
    config?.customer && typeof config.customer === "object"
      ? (config.customer as Record<string, unknown>)
      : schedule.customer && typeof schedule.customer === "object"
        ? (schedule.customer as Record<string, unknown>)
        : undefined;
  const noa =
    (root.noa && typeof root.noa === "object"
      ? root.noa
      : schedule.noa && typeof schedule.noa === "object"
        ? schedule.noa
        : undefined) as Record<string, unknown> | undefined;
  const upfront =
    (root.upfront && typeof root.upfront === "object"
      ? root.upfront
      : schedule.upfront && typeof schedule.upfront === "object"
        ? schedule.upfront
        : undefined) as Record<string, unknown> | undefined;
  const paymentSummary =
    (root.paymentSummary && typeof root.paymentSummary === "object"
      ? root.paymentSummary
      : root.payment_summary && typeof root.payment_summary === "object"
        ? root.payment_summary
        : root.summary && typeof root.summary === "object"
          ? root.summary
          : undefined) as Record<string, unknown> | undefined;
  const totals =
    (root.totals && typeof root.totals === "object"
      ? root.totals
      : root.waterfall &&
          typeof root.waterfall === "object" &&
          (root.waterfall as Record<string, unknown>).totals
        ? ((root.waterfall as Record<string, unknown>).totals as Record<
            string,
            unknown
          >)
        : undefined) as Record<string, unknown> | undefined;
  const assumptions =
    (root.assumptions && typeof root.assumptions === "object"
      ? root.assumptions
      : root.waterfall &&
          typeof root.waterfall === "object" &&
          (root.waterfall as Record<string, unknown>).assumptions
        ? ((root.waterfall as Record<string, unknown>).assumptions as Record<
            string,
            unknown
          >)
        : undefined) as Record<string, unknown> | undefined;

  const id = String(
    portfolioPick(
      schedule,
      "id",
      "rentalScheduleId",
      "rental_schedule_id",
      "scheduleId",
      "schedule_id"
    ) ?? ""
  );
  if (!id) return null;

  const displayId = String(
    portfolioPick(nestedSchedule ?? {}, "displayId", "display_id") ??
      portfolioPick(
        schedule,
        "displayId",
        "display_id",
        "rsNo",
        "rs_no",
        "rentalScheduleCode",
        "rental_schedule_code"
      ) ??
      portfolioPick(root, "displayId", "display_id", "rsNo", "rs_no") ??
      ""
  );

  const linesSource = Array.isArray(root.lines)
    ? root.lines
    : Array.isArray(schedule.lines)
      ? schedule.lines
      : [];

  const monthlyLine = linesSource.find(
    (line) =>
      line &&
      typeof line === "object" &&
      !(line as Record<string, unknown>).isInterim &&
      !(line as Record<string, unknown>).is_interim
  ) as Record<string, unknown> | undefined;

  const upfrontReceivedFromCharges =
    portfolioNum(
      upfront?.processingFeeRounded ?? upfront?.processing_fee_rounded
    ) +
    portfolioNum(upfront?.feeGSTRounded ?? upfront?.fee_gst_rounded) +
    portfolioNum(
      upfront?.securityDepositAmountRounded ??
        upfront?.security_deposit_amount_rounded
    );

  const upfrontRevenueFromCharges =
    portfolioNum(
      upfront?.processingFeeRounded ?? upfront?.processing_fee_rounded
    ) + portfolioNum(upfront?.feeGSTRounded ?? upfront?.fee_gst_rounded);

  const assetItc = portfolioNum(
    portfolioPick(assumptions ?? {}, "assetItc", "asset_itc")
  );
  const pfGst = portfolioNum(
    portfolioPick(assumptions ?? {}, "pfGst", "pf_gst")
  );

  return {
    id,
    displayId,
    status: String(schedule.status ?? root.status ?? ""),
    customerName: String(
      portfolioPick(root, "customerName", "customer_name") ??
        portfolioPick(customer ?? {}, "company_name", "companyName", "name") ??
        ""
    ),
    creditRating: String(
      portfolioPick(root, "creditRating", "credit_rating") ?? "Not Rated"
    ),
    totalRentalSchedules: portfolioNum(
      portfolioPick(
        root,
        "totalRentalSchedules",
        "total_rental_schedules",
        "totalRentalSchedulesForCustomer"
      )
    ),
    nextDueDate:
      (portfolioPick(root, "nextDueDate", "next_due_date") as string | undefined) ??
      null,
    monthlyRental: portfolioNum(
      portfolioPick(root, "monthlyRental", "monthly_rental") ??
        (monthlyLine
          ? portfolioPick(monthlyLine, "baseRentRounded", "base_rent_rounded")
          : undefined)
    ),
    fundingType: String(
      portfolioPick(config ?? {}, "fundingType", "funding_type") ?? "—"
    ),
    lenderName:
      portfolioPick(noa ?? {}, "lenderName", "lender_name", "lender") != null
        ? String(
            portfolioPick(noa ?? {}, "lenderName", "lender_name", "lender")
          )
        : null,
    disbursementDate:
      (portfolioPick(
        noa ?? {},
        "disbursementDate",
        "disbursement_date"
      ) as string | undefined) ?? null,
    disbursementAmount: portfolioNum(
      portfolioPick(noa ?? {}, "disbursementAmount", "disbursement_amount")
    ),
    tenureMonths: portfolioNum(
      portfolioPick(config ?? schedule, "tenureMonths", "tenure_months")
    ),
    upfrontReceived:
      portfolioNum(
        portfolioPick(root, "upfrontReceived", "upfront_received")
      ) || upfrontReceivedFromCharges,
    upfrontRevenue:
      portfolioNum(portfolioPick(root, "upfrontRevenue", "upfront_revenue")) ||
      upfrontRevenueFromCharges,
    totalBilling: portfolioNum(
      portfolioPick(paymentSummary ?? root, "totalBilling", "total_billing")
    ),
    totalReceived: portfolioNum(
      portfolioPick(paymentSummary ?? root, "totalReceived", "total_received")
    ),
    totalOutstanding: portfolioNum(
      portfolioPick(
        paymentSummary ?? root,
        "totalOutstanding",
        "total_outstanding"
      )
    ),
    tdsTotal: portfolioNum(
      portfolioPick(paymentSummary ?? totals ?? root, "tdsTotal", "tds_total")
    ),
    itcInflow: portfolioNum(
      portfolioPick(paymentSummary ?? root, "itcInflow", "itc_inflow")
    ) || assetItc + pfGst,
    itcUtilised: portfolioNum(
      portfolioPick(
        paymentSummary ?? totals ?? root,
        "itcUtilised",
        "itc_utilised",
        "itcUsedTotal",
        "itc_used_total"
      )
    ),
    revenueBookedToDate: portfolioNum(
      portfolioPick(
        paymentSummary ?? totals ?? root,
        "revenueBookedToDate",
        "revenue_booked_to_date"
      )
    ),
    revenueThisTranche: portfolioNum(
      portfolioPick(
        paymentSummary ?? totals ?? root,
        "revenueThisTranche",
        "revenue_this_tranche"
      )
    ),
    capitalDeployed: portfolioNum(
      portfolioPick(
        paymentSummary ?? totals ?? root,
        "capitalDeployed",
        "capital_deployed"
      )
    ),
    capitalRecovered: portfolioNum(
      portfolioPick(
        paymentSummary ?? totals ?? root,
        "capitalRecovered",
        "capital_recovered"
      )
    ),
    lines: linesSource.map((line) =>
      normalizePortfolioRSLine(line as Record<string, unknown>)
    ),
  };
}

/** Resolve a schedule from a portfolio route param (internal id, display id, or RS code). */
export function resolvePortfolioSchedule(
  schedules: Schedule[],
  routeId?: string | null
): Schedule | undefined {
  if (!routeId) return undefined;
  const normalized = decodeURIComponent(routeId).trim();
  if (!normalized) return undefined;

  const withoutRsPrefix = normalized.replace(/^RS[-\s]?/i, "");

  return schedules.find((schedule) => {
    const id = String(schedule.id ?? "");
    const displayId = String(schedule.displayId ?? "");
    const rentalScheduleCode = String(schedule.rentalScheduleCode ?? "");

    return (
      id === normalized ||
      displayId === normalized ||
      displayId === withoutRsPrefix ||
      rentalScheduleCode === normalized ||
      rentalScheduleCode.replace(/^RS[-\s]?/i, "") === withoutRsPrefix
    );
  });
}

export function buildPortfolioRows(
  schedules: Schedule[],
  invoices: Invoice[],
  noAs: NoticeOfAssignment[]
): PortfolioRowData[] {
  const today = new Date();
  const mStart = startOfMonth(today);
  const mEnd = endOfMonth(today);
  return schedules
    .filter(isActiveRS)
    .map((rs) => {
      const noa = noAs.find((n) => n.scheduleId === rs.id);
      const invs = invoicesForRS(invoices, rs.id);
      const upfront = (rs.upfront?.processingFeeRounded || 0) + (rs.upfront?.feeGSTRounded || 0);
      return {
        rs,
        noa,
        monthlyRental: monthlyRental(rs),
        tenureMonths: rs.config.tenureMonths,
        rentalStartDate: rs.config.commencementDate,
        upfrontRevenue: upfront,
        cashflowThisMonth: astraCashflowInWindow(invs, mStart, mEnd),
        cashflowToDate: astraCashflowToDate(invs),
        status: rs.status,
      };
    })
    .sort((a, b) => a.rs.config.customer.name.localeCompare(b.rs.config.customer.name));
}
